import axiosInstance from '../../utils/axiosInstance';
import {
  FinancialRecordRequestDto,
  FinancialRecordResponseDto,
  FinancialRecordType,
  FinancialSummary,
} from '../../model/Financial.model';

export const FinancialService = {
  /**
   * Creates a new financial record
   * @param record The financial record data
   * @returns The created financial record
   */
  createRecord: async (
    record: FinancialRecordRequestDto
  ): Promise<FinancialRecordResponseDto> => {
    const response = await axiosInstance.post(`/finance/create`, record);
    return response.data;
  },

  /**
   * Gets all financial records with optional filters
   * @param recordType Optional type filter (INCOME or EXPENSE)
   * @param month Optional month filter (1-12)
   * @param year Optional year filter
   * @returns Array of financial records
   */
  getAllRecords: async (
    recordType?: string,
    month?: number,
    year?: number
  ): Promise<FinancialRecordResponseDto[]> => {
    try {
      const params: any = {};
      if (recordType) params.recordType = recordType;
      if (month) params.month = month;
      if (year) params.year = year;

      const response = await axiosInstance.get(`/finance`, { params });
      return response.data;
    } catch (error) {
      console.error('Error fetching all records:', error);
      throw error;
    }
  },

  /**
   * Gets all financial records for a specific employee
   * @param employeeId The employee ID
   * @returns Array of financial records
   */
  getRecordsByEmployeeId: async (
    employeeId: number
  ): Promise<FinancialRecordResponseDto[]> => {
    const response = await axiosInstance.get(
      `/finance/employee/${employeeId}`
    );
    return response.data;
  },

  /**
   * Gets a financial record by its ID
   * @param id The record ID
   * @returns The financial record
   */
  getRecordById: async (id: number): Promise<FinancialRecordResponseDto> => {
    const response = await axiosInstance.get(`/finance/${id}`);
    return response.data;
  },

  /**
   * Updates an existing financial record
   * @param id The record ID
   * @param record The updated record data
   * @returns The updated financial record
   */
  updateRecord: async (
    id: number,
    record: FinancialRecordRequestDto
  ): Promise<FinancialRecordResponseDto> => {
    const response = await axiosInstance.put(`/finance/${id}`, record);
    return response.data;
  },

  /**
   * Deletes a financial record
   * @param id The record ID
   * @returns Success message
   */
  deleteRecord: async (id: number): Promise<string> => {
    const response = await axiosInstance.delete(`/finance/${id}`);
    return response.data;
  },

  /**
   * Gets all deleted financial records
   * @returns Array of deleted financial records
   */
  getDeletedRecords: async (): Promise<FinancialRecordResponseDto[]> => {
    const response = await axiosInstance.get(`/finance/deleted`);
    return response.data;
  },

  /**
   * Gets financial summary by month and year
   * @param month The month (1-12)
   * @param year The year
   * @returns Financial summary
   */
  getFinancialSummary: async (
    month: number,
    year: number
  ): Promise<FinancialSummary> => {
    const response = await axiosInstance.get(`/finance/summary`, {
      params: { month, year },
    });
    return response.data;
  },

  /**
   * Gets financial records by transaction ID
   * @param transactionId The transaction ID
   * @returns Array of financial records related to the transaction
   */
  getRecordsByTransactionId: async (
    transactionId: number
  ): Promise<FinancialRecordResponseDto[]> => {
    const response = await axiosInstance.get(
      `/finance/by-transaction/${transactionId}`
    );
    return response.data;
  },

  /**
   * Gets financial records with relatedTransactionId for a specific date
   * @param date Date in YYYY-MM-DD format
   * @returns Array of financial records with relatedTransactionId
   */
  getRecordsWithRelatedTransactionByDate: async (
    date: string
  ): Promise<FinancialRecordResponseDto[]> => {
    const response = await axiosInstance.get(`/finance/by-date`, {
      params: { date },
    });
    return response.data;
  },

  /**
   * Gets financial records with relatedTransactionId for a specific date and employee
   * @param date Date in YYYY-MM-DD format
   * @param employeeId The employee ID
   * @returns Array of financial records with relatedTransactionId for the employee
   */
  getRecordsWithRelatedTransactionByDateAndEmployee: async (
    date: string,
    employeeId: number
  ): Promise<FinancialRecordResponseDto[]> => {
    const response = await axiosInstance.get(
      `/finance/by-date-and-employee`,
      {
        params: { date, employeeId },
      }
    );
    return response.data;
  },
};
